/**
 * Moteur de rendu : crée la scène, orchestre les couches et tient la boucle
 * d'image. Il ne touche jamais au réseau : il lit le store (réplique) et
 * l'horloge estimée, et prédit les positions pour l'instant affiché.
 */
import * as THREE from 'three/webgpu';
import { createTerrainFunction, minuteOfDay, type GameMap, type Heightfield, type Piste } from '@snowchain/shared';
import type { ClientStore } from '../state/store';
import { ui } from '../hud/ui-state';
import type { AssetLibrary } from './assets';
import { RtsCamera, type CameraHome } from './camera';
import { Terrain, pisteOverlay } from './terrain';
import { PisteRaster } from './piste-raster';
import { SkySystem } from './sky';
import { Decor } from './decor';
import { BuildingsLayer } from './buildings';
import { LiftsLayer } from './lifts';
import { SkiersLayer } from './skiers';
import { GroomersLayer, groomerSpots } from './groomers';
import { groomSurface } from './groom-surface';
import { Snowfall } from './snowfall';
import { Interaction } from './interaction';
import { PisteTool } from './piste-tool';
import { PisteHighlight } from './piste-highlight';
import { Frustum } from './instancing';
import { PerfMonitor } from './perf';
import { createApron } from './apron';
import { Earthworks, type KeepOut } from './earthworks';

export interface RenderContext {
  renderer: THREE.WebGPURenderer;
  backend: 'webgpu' | 'webgl2';
}

/**
 * Cadrage de départ : celui de la carte s'il est décrit, sinon depuis le
 * village, le regard tourné vers la montagne qui le domine.
 */
export function homeView(map: GameMap, hf: Heightfield): CameraHome {
  if (map.view) return map.view;
  const v = map.features.village;
  let best = -Infinity;
  let dir: [number, number] = [0, -1];
  for (let k = 0; k < 32; k++) {
    const a = (k / 32) * Math.PI * 2;
    const dx = Math.sin(a);
    const dz = Math.cos(a);
    const h = hf.height(v.x + dx * 700, v.z + dz * 700);
    if (h > best) {
      best = h;
      dir = [dx, dz];
    }
  }
  return { x: v.x + dir[0] * 270, z: v.z + dir[1] * 270, yaw: Math.atan2(-dir[0], -dir[1]) + 0.42 };
}

export async function createRenderer(canvas: HTMLCanvasElement): Promise<RenderContext> {
  const params = new URLSearchParams(location.search);
  const forceWebGL = params.get('renderer') === 'webgl';
  const renderer = new THREE.WebGPURenderer({ canvas, antialias: true, forceWebGL, powerPreference: 'high-performance', trackTimestamp: params.has('gputime') });
  await renderer.init();
  const backend = (renderer.backend as unknown as { isWebGPUBackend?: boolean }).isWebGPUBackend ? 'webgpu' : 'webgl2';
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
  renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  return { renderer, backend };
}

export class Engine {
  readonly scene = new THREE.Scene();
  readonly cam: RtsCamera;
  readonly terrain: Terrain;
  readonly raster: PisteRaster;
  readonly sky: SkySystem;
  readonly decor: Decor;
  readonly buildings: BuildingsLayer;
  readonly lifts: LiftsLayer;
  readonly skiers: SkiersLayer;
  readonly groomers: GroomersLayer;
  readonly snowfall: Snowfall;
  readonly interaction: Interaction;
  /** Tracé et reprise des pistes (aperçu, poignées). */
  readonly pisteTool: PisteTool;
  /** Surlignage de la piste sélectionnée. */
  readonly pisteHighlight: PisteHighlight;
  readonly perf: PerfMonitor;
  /** Relief de rendu terrassé sous les bâtiments ; le jeu garde le relief naturel (store.hf). */
  readonly earthworks: Earthworks;
  private readonly frustum = new Frustum();
  private seen = { lifts: -1, pistes: -1, buildings: -1, world: -1 };
  private structureKey = '';
  private snowKey = '';
  private lastCamMatrix = new Float32Array(16);
  private frameNo = 0;
  private lastFrame = performance.now();
  private decorTimer: ReturnType<typeof setTimeout> | null = null;
  private overlay = 0;
  occlusionEnabled = !new URLSearchParams(location.search).has('noocclusion');
  private readonly gpuTiming = new URLSearchParams(location.search).has('gputime');
  private gpuFrames = 0;
  private gpuResolving = false;
  /** Temps GPU moyen par image (ms), si les timestamps sont disponibles. */
  gpuMs = 0;

  constructor(
    readonly ctx: RenderContext,
    private readonly canvas: HTMLCanvasElement,
    private readonly store: ClientStore,
    readonly lib: AssetLibrary,
  ) {
    // Plateformes posées avant la première tuile de terrain : rien à refaire au démarrage.
    this.earthworks = new Earthworks(store.hf);
    if (store.replica) this.earthworks.apply(store.replica.buildings.values(), this.keepOut());
    const hf = this.earthworks.hf;
    // Grilles des pistes et du damage à la taille de la carte, avant le matériau du terrain qui les lit.
    this.raster = new PisteRaster(hf);
    groomSurface.configure(hf);
    this.cam = new RtsCamera(canvas, hf, homeView(store.map, hf));
    this.cam.overviewFrame = () => this.overviewFrame();
    this.scene.name = 'snowchain';
    this.sky = new SkySystem(this.scene);
    this.terrain = new Terrain(hf, this.raster, store.map.features.village);
    this.scene.add(this.terrain.group);
    this.addLake();
    this.scene.add(createApron(hf, store.map.features.relief ? createTerrainFunction(store.map.features) : undefined));
    this.addBackdrop();
    this.decor = new Decor(lib, hf, store.map);
    this.scene.add(this.decor.group);
    this.buildings = new BuildingsLayer(lib, hf, (b) => this.earthworks.seat(b));
    this.scene.add(this.buildings.group);
    this.lifts = new LiftsLayer(lib, hf);
    this.scene.add(this.lifts.group);
    this.skiers = new SkiersLayer(lib, hf);
    this.scene.add(this.skiers.group);
    this.groomers = new GroomersLayer(lib, hf);
    this.scene.add(this.groomers.group);
    // Un clic ou un glisser sur la carte reprend la main sur la caméra qui suivait une dameuse.
    canvas.addEventListener('pointerdown', () => {
      if (ui.follow.value !== null) ui.follow.value = null;
    });
    this.snowfall = new Snowfall(this.scene);
    this.interaction = new Interaction(canvas, this.cam, store, lib, this.raster);
    this.scene.add(this.interaction.group);
    this.pisteTool = new PisteTool(canvas, this.cam, store, this.earthworks.hf);
    this.scene.add(this.pisteTool.group);
    this.frameHooks.add(() => this.pisteTool.frame());
    this.pisteHighlight = new PisteHighlight(this.cam, store, this.earthworks.hf);
    this.scene.add(this.pisteHighlight.group);
    this.frameHooks.add(() => this.pisteHighlight.frame());
    this.perf = new PerfMonitor(ctx.renderer);
    window.addEventListener('resize', () => this.resize());
    this.resize();
    ui.build.subscribe(() => this.interaction.onBuildChange());
    ui.freeCam.subscribe((v) => this.cam.setFree(v));
  }

  private addLake(): void {
    const lake = this.store.map.features.lake;
    const geo = new THREE.CircleGeometry(lake.radius + 4, 48);
    geo.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshStandardNodeMaterial({ color: 0x9cc2d4, roughness: 0.15, metalness: 0 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(lake.x, this.store.hf.waterLevel, lake.z);
    mesh.receiveShadow = true;
    mesh.name = 'lac';
    this.scene.add(mesh);
  }

  private addBackdrop(): void {
    if (!this.lib.has('backdrop_mountains')) return;
    const g = this.lib.get('backdrop_mountains').geometries[0] as THREE.BufferGeometry;
    const mesh = new THREE.Mesh(g, this.lib.plain);
    mesh.position.set(0, 0, 0);
    // Anneau dessiné pour Val Cendrée (rayon intérieur 3,5 km) : élargi au-delà du tablier des grandes cartes.
    const s = Math.max(1, (this.store.hf.half + 1500) / 2524);
    mesh.scale.set(s, 1 + (s - 1) * 0.6, s);
    mesh.name = 'montagnes';
    mesh.frustumCulled = false;
    this.scene.add(mesh);
  }

  /** Vue d'ensemble : le domaine (village, remontées, pistes) vu de haut, en entier. */
  private overviewFrame(): [number, number, number] {
    const v = this.store.map.features.village;
    let minX = v.x - v.radius;
    let maxX = v.x + v.radius;
    let minZ = v.z - v.radius;
    let maxZ = v.z + v.radius;
    const add = (p: readonly [number, number]) => {
      if (p[0] < minX) minX = p[0];
      if (p[0] > maxX) maxX = p[0];
      if (p[1] < minZ) minZ = p[1];
      if (p[1] > maxZ) maxZ = p[1];
    };
    const r = this.store.replica;
    for (const l of r?.lifts.values() ?? []) {
      add(l.bottom);
      add(l.top);
    }
    for (const p of r?.pistes.values() ?? []) for (const q of p.path) add(q);
    // Champ vertical ~0,93 x recul, horizontal ~1,5 x recul (vue plongeante, 50°).
    const dist = Math.max(2300, Math.min(this.cam.maxDist, 1.1 * Math.max((maxZ - minZ) / 0.93, (maxX - minX) / 1.5)));
    return [(minX + maxX) / 2, (minZ + maxZ) / 2 - 60, dist];
  }

  private resize(): void {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    this.ctx.renderer.setSize(w, h, false);
    this.cam.resize(w, h);
  }

  start(): void {
    this.ctx.renderer.setAnimationLoop(() => this.frame());
  }

  /** Coordonnées écran (px) d'un point au sol : utile aux tests automatisés. */
  worldToScreen(x: number, z: number): { x: number; y: number } {
    const v = new THREE.Vector3(x, this.store.hf.height(x, z), z).project(this.cam.camera);
    const rect = this.canvas.getBoundingClientRect();
    return { x: rect.left + ((v.x + 1) / 2) * rect.width, y: rect.top + ((1 - v.y) / 2) * rect.height };
  }

  private readonly projV = new THREE.Vector3();
  /** Rappels appelés après chaque image (épingles du HUD, positionnées sur la caméra du moment). */
  readonly frameHooks = new Set<() => void>();

  /**
   * Projection d'un point (x, hauteur au-dessus du sol, z) dans le canevas :
   * pixels, distance à la caméra, et faux s'il est derrière la caméra.
   */
  project(x: number, lift: number, z: number, out: { x: number; y: number; dist: number; front: boolean }): void {
    const v = this.projV.set(x, this.store.hf.height(x, z) + lift, z);
    out.dist = v.distanceTo(this.cam.camera.position);
    v.project(this.cam.camera);
    out.front = v.z < 1;
    out.x = ((v.x + 1) / 2) * this.canvas.clientWidth;
    out.y = ((1 - v.y) / 2) * this.canvas.clientHeight;
  }

  /** Synchronise les couches statiques avec l'état du store. */
  private syncStructure(): boolean {
    const r = this.store.replica;
    if (!r) return false;
    const v = this.store.versions;
    let staticChanged = false;
    if (v.pistes !== this.seen.pistes) {
      this.seen.pistes = v.pistes;
      // Le raster met aussi à jour la texture d'état de la neige lue par le shader.
      if (this.raster.update(r.pistes.values())) this.scheduleDecor();
      groomSurface.updateGeometry(r.pistes.values(), (id) => this.store.pisteTrack(id));
      // L'enneigement naturel (roche apparente) suit la moyenne des pistes, par paliers de 10 cm.
      const depths = [...r.pistes.values()].map((p: Piste) => p.snowDepth);
      const natural = depths.length ? depths.reduce((a, b) => a + b, 0) / depths.length : 60;
      const snowKey = String(Math.round(natural / 10));
      if (snowKey !== this.snowKey) {
        this.snowKey = snowKey;
        this.terrain.naturalSnow = natural;
        this.terrain.markAllDirty();
      }
    }
    // Terrassements d'abord : terrain, bâtiments et décor se posent ensuite sur le relief aplani.
    let earthChanged = false;
    if (v.buildings !== this.seen.buildings || v.lifts !== this.seen.lifts) {
      const rect = this.earthworks.apply(r.buildings.values(), this.keepOut());
      if (rect) {
        this.terrain.markDirtyRect(rect);
        earthChanged = true;
      }
    }
    if (v.lifts !== this.seen.lifts) {
      this.seen.lifts = v.lifts;
      this.lifts.rebuild(this.store);
      staticChanged = true;
    }
    if (v.buildings !== this.seen.buildings || v.pistes !== this.seen.pistes || earthChanged) {
      this.seen.buildings = v.buildings;
      this.buildings.rebuild(r.buildings.values(), r.pistes);
      staticChanged = true;
    }
    const key = `${[...r.lifts.keys()].join(',')}|${[...r.buildings.keys()].join(',')}`;
    if (key !== this.structureKey || earthChanged) {
      this.structureKey = key;
      this.scheduleDecor();
    }
    return staticChanged;
  }

  /** Abords des gares et des pylônes, posés sur le relief naturel : jamais terrassés. */
  private keepOut(): KeepOut[] {
    const out: KeepOut[] = [];
    const r = this.store.replica;
    if (!r) return out;
    for (const l of r.lifts.values()) {
      out.push([l.bottom[0], l.bottom[1], 14], [l.top[0], l.top[1], 14]);
      const geo = this.store.liftGeometry(l.id);
      if (geo) for (const p of geo.pylons) out.push([p.x, p.z, 4]);
    }
    return out;
  }

  private scheduleDecor(): void {
    if (this.decorTimer) return;
    this.decorTimer = setTimeout(() => {
      this.decorTimer = null;
      const r = this.store.replica;
      if (!r) return;
      this.decor.rebuild(this.raster, r.lifts.values(), r.buildings.values(), r.pistes.values());
      this.forceStatic = true;
    }, 30);
  }

  private forceStatic = true;
  /** Profil CPU par étape de l'image (ms, moyenne sur la dernière seconde) ; actif avec ?perf. */
  readonly profile: Record<string, number> = {};
  private readonly profiling = new URLSearchParams(location.search).has('perf');
  private profAcc: Record<string, number> = {};
  private profFrames = 0;
  private profT = 0;

  private mark(step: string): void {
    if (!this.profiling) return;
    const now = performance.now();
    this.profAcc[step] = (this.profAcc[step] ?? 0) + (now - this.profT);
    this.profT = now;
  }

  private cameraMoved(): boolean {
    const e = this.cam.camera.matrixWorld.elements;
    let diff = 0;
    for (let i = 0; i < 16; i++) diff += Math.abs((e[i] as number) - (this.lastCamMatrix[i] as number));
    if (diff > 1e-4) {
      this.lastCamMatrix.set(e);
      return true;
    }
    return false;
  }

  private frame(): void {
    const t0 = performance.now();
    const dt = Math.min(0.1, (t0 - this.lastFrame) / 1000);
    const dtMs = t0 - this.lastFrame;
    this.lastFrame = t0;
    this.frameNo++;
    this.profT = t0;
    if (this.profiling) this.profFrames++;
    const store = this.store;
    const renderer = this.ctx.renderer;
    this.cam.update(dt);
    const camera = this.cam.camera;
    this.frustum.setFromCamera(camera);
    const moved = this.cameraMoved();
    if (this.occlusionEnabled && (moved || this.frameNo % 30 === 0) && this.frameNo % 6 === 0) this.terrain.updateOcclusion(camera);
    const visChanged = this.terrain.update(camera, this.frustum, this.occlusionEnabled);
    const staticChanged = this.syncStructure();
    this.terrain.refreshDirty(2);
    // Le décor suit la visibilité des tuiles : on le retrie aussi quand l'occlusion change sans mouvement.
    if (moved || staticChanged || visChanged || this.forceStatic) {
      this.forceStatic = false;
      this.decor.update(camera, this.frustum, this.terrain);
      this.buildings.update(camera, this.frustum);
      this.lifts.updateStatic(camera, this.frustum);
    }
    this.mark('scene');
    const tSim = store.clock.renderSim(t0);
    this.lifts.updateCarriers(store, tSim, camera, this.frustum);
    this.mark('remontees');
    this.skiers.update(store, tSim, camera, this.frustum, this.terrain);
    this.mark('skieurs');
    // Dameuses : mouvement daté en temps de jeu, affiché avec le même retard que les skieurs.
    const tGame = store.clock.gameNow(t0 - store.clock.delay * 1000);
    this.groomers.update(store, tGame, dt, camera, this.frustum, this.terrain);
    if (store.replica) groomSurface.updateState(store.replica.pistes, store.replica.groomers.values(), tGame);
    const follow = ui.follow.value;
    if (follow !== null) {
      const spot = groomerSpots.get(follow);
      if (spot) this.cam.focus(spot[0], spot[1]);
      else if (!store.replica?.groomers.has(follow)) ui.follow.value = null;
    }
    this.mark('dameuses');
    const weather = store.replica?.world.weather ?? null;
    const minute = minuteOfDay(store.clock.gameNow(t0));
    this.sky.update(minute, weather, this.cam.target, camera, this.cam.distance * 0.75);
    renderer.toneMappingExposure = this.sky.exposure;
    this.snowfall.update(dt, camera, weather?.snowRate ?? 0, weather?.wind ?? 0, (weather?.snowRate ?? 0) > 0.05 && this.cam.distance < 1400);
    const target = ui.overlay.value ? 1 : 0;
    this.overlay += (target - this.overlay) * Math.min(1, dt * 6);
    pisteOverlay.value = this.overlay;
    this.mark('ciel');
    renderer.render(this.scene, camera);
    this.mark('rendu');
    for (const hook of this.frameHooks) hook();
    if (this.gpuTiming) {
      this.gpuFrames++;
      if (!this.gpuResolving && this.gpuFrames >= 10) {
        const frames = this.gpuFrames;
        this.gpuFrames = 0;
        this.gpuResolving = true;
        renderer
          .resolveTimestampsAsync('render')
          .then((ms) => {
            if (typeof ms === 'number' && ms > 0) this.gpuMs = ms / frames;
          })
          .catch(() => undefined)
          .finally(() => (this.gpuResolving = false));
      }
    }
    const cpu = performance.now() - t0;
    const s = this.perf.frame(dtMs, cpu);
    if (s) {
      if (this.profiling && this.profFrames > 0) {
        for (const k of Object.keys(this.profAcc)) {
          this.profile[k] = Math.round(((this.profAcc[k] as number) / this.profFrames) * 1000) / 1000;
          this.profAcc[k] = 0;
        }
        this.profFrames = 0;
      }
      const sk = this.skiers.stats();
      const de = this.decor.stats();
      const bu = this.buildings.stats();
      const li = this.lifts.stats();
      ui.perfStats.value = {
        ...s,
        backend: this.ctx.backend === 'webgpu' ? 'WebGPU' : 'WebGL2',
        skiers: this.skiers.drawn,
        skierTris: sk.triangles,
        decor: de.instances,
        decorTris: de.triangles,
        trees: this.decor.treeCount,
        houses: this.decor.houseCount,
        poles: this.decor.poleCount,
        buildings: bu.instances + li.instances,
        terrainTris: this.terrain.trianglesDrawn,
        chunks: this.terrain.chunksDrawn,
        occluded: this.terrain.occludedCount,
        agents: store.replica?.agents.size ?? 0,
        carriers: this.lifts.carriersDrawn,
        gpuMs: Math.round(this.gpuMs * 100) / 100,
      };
    }
  }
}
